/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { CheckListPlugin } from "@lexical/react/LexicalCheckListPlugin";
import LexicalClickableLinkPlugin from "@lexical/react/LexicalClickableLinkPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { HorizontalRulePlugin } from "@lexical/react/LexicalHorizontalRulePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import useLexicalEditable from "@lexical/react/useLexicalEditable";
import { useEffect, useRef, useState } from "react";

import { AiWizardDialog } from "@/features/editor/components/AiWizardDialog";
import { EditorConflictDialog } from "@/features/editor/components/EditorConflictDialog";
import { $generateNodesFromDOM } from "@lexical/html";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { create } from "zustand";
import { FraseDocument } from "../../features/documents";
import { useEditorStore } from "../../stores/editor";
import { useSidebarNavigationStore } from "../Layout/SidebarToggle";
import { useSettings } from "./context/SettingsContext";
import TableCellNodes from "./nodes/TableCellNodes";
import AiWriterMenuPlugin from "./plugins/AiWriterPlugin";
import { AutoSaveDocument } from "./plugins/AutoSavePlugin/AutoSaveDocument";
import CodeHighlightPlugin from "./plugins/CodeHighlightPlugin";
import CollapsiblePlugin from "./plugins/CollapsiblePlugin";
import ComponentPickerPlugin from "./plugins/ComponentPickerPlugin";
import ContinueWritingPlugin from "./plugins/ContinueWritingPlugin";
import DragDropPaste from "./plugins/DragDropPastePlugin";
import DraggableBlockPlugin from "./plugins/DraggableBlockPlugin";
import { EditorStorePlugin } from "./plugins/EditorStorePlugin";
import FloatingLinkEditorPlugin from "./plugins/FloatingLinkEditorPlugin";
import FloatingTextFormatToolbarPlugin from "./plugins/FloatingTextFormatToolbarPlugin";
import ImagesPlugin from "./plugins/ImagesPlugin";
import { ImportUrlBadgePlugin } from "./plugins/ImportUrlBadgePlugin/ImportUrlBadgePlugin";
import LinePlaceholderPlugin from "./plugins/LinePlaceholderPlugin";
import LinkPlugin from "./plugins/LinkPlugin";
import ListMaxIndentLevelPlugin from "./plugins/ListMaxIndentLevelPlugin";
import MarkdownShortcutPlugin from "./plugins/MarkdownShortcutPlugin";
import MarkdownTransformerPlugin from "./plugins/MarkdownTransformerPlugin";
import OnChangePlugin from "./plugins/OnChangePlugin";
import PlaceholderActionsPlugin from "./plugins/PlaceholderActionsPlugin";
import PrepopulateDocument from "./plugins/PrepopulatePlugin/PrepopulateDocument";
import TabFocusPlugin from "./plugins/TabFocusPlugin";
import TableCellActionMenuPlugin from "./plugins/TableActionMenuPlugin";
import TableCellResizer from "./plugins/TableCellResizer";
import { TopicHighlightPlugin } from "./plugins/TopicHighlightPlugin/TopicHighlightPlugin";
import TopicScorePlugin from "./plugins/TopicScorePlugin";
import WordCountPlugin from "./plugins/WordCountPlugin";
import PlaygroundEditorTheme from "./themes/PlaygroundEditorTheme";
import ContentEditable from "./ui/ContentEditable";
import { TitleInput } from "./ui/TitleInput";

const CONTENT_LABEL = "tab 1";

export const usePlaceholderStore = create((set) => ({
  showPlaceholder: false,
  setShowPlaceholder: (value: boolean) => set({ showPlaceholder: value }),
  showLinePlaceholder: true,
  setShowLinePlaceholder: (value: boolean) =>
    set({ showLinePlaceholder: value }),
}));

export const DocumentEditor = ({
  documentMetadata,
  isReadOnly,
  editorRef,
}: {
  documentMetadata: FraseDocument;
  isReadOnly: boolean;
  editorRef: React.MutableRefObject<any>;
}) => {
  const {
    settings: { tableCellMerge, tableCellBackgroundColor },
  } = useSettings();
  const [editor] = useLexicalComposerContext();
  const isEditable = useLexicalEditable();
  const [floatingAnchorElem, setFloatingAnchorElem] =
    useState<HTMLDivElement | null>(null);
  const [blockElem, setBlockElem] = useState<HTMLDivElement | null>(null);
  const linePlaceholderRef = useRef<HTMLDivElement>(null);
  const [showAiWriter, setShowAiWriter] = useState<boolean>(false);
  const [isRewriting, setIsRewriting] = useState<boolean>(false);
  const {
    showPlaceholder,
    setShowPlaceholder,
    showLinePlaceholder,
    setShowLinePlaceholder,
  } = usePlaceholderStore();
  const [lastSelection, setLastSelection] = useState<Range | null>(null);
  const { editor: editorStore, setEditor } = useEditorStore();
  const { activeTabIndex, isPasting } = editorStore;
  const { closeSidebar, isSidebarOpen } = useSidebarNavigationStore();
  const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);

  useEffect(() => {
    if (isReadOnly && isSidebarOpen) {
      closeSidebar();
    }
  }, [isReadOnly, isSidebarOpen]);

  const titleInputRef = useRef<HTMLTextAreaElement>(null);
  const floatingRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  const blockRef = (_blockElem: HTMLDivElement) => {
    if (_blockElem !== null) {
      setBlockElem(_blockElem);
    }
  };

  const cellEditorConfig = {
    namespace: "Playground",
    nodes: [...TableCellNodes],
    onError: (error: Error) => {
      throw error;
    },
    theme: PlaygroundEditorTheme,
  };

  useEffect(() => {
    editor.update(() => {
      if (!documentMetadata || documentMetadata.text === null) {
        return;
      }

      let documentContent = {
        html: "",
        title: "",
        name: CONTENT_LABEL,
      };
      documentContent =
        documentMetadata.text[activeTabIndex] || documentContent;

      const parser = new DOMParser();
      const dom = parser.parseFromString(documentContent.html, "text/html");

      let nodes = $generateNodesFromDOM(editor, dom);
      let textContent = "";
      nodes.forEach((node) => {
        textContent += node.getTextContent();
      });
      const nodesLength = nodes.length;
      if (textContent.length === 0 && nodesLength <= 1 && !isPasting) {
        setShowLinePlaceholder(false);
        setShowPlaceholder(true);
      } else {
        editor.focus();
        setShowPlaceholder(false);
        setShowLinePlaceholder(true);
      }
    });
  }, [
    documentMetadata.hash,
    activeTabIndex,
    documentMetadata.text.length,
    isPasting,
  ]);

  return (
    <div ref={editorRef} className=" flex flex-col h-full w-full">
      <AiWizardDialog />
      <EditorConflictDialog />
      <div className="flex flex-col h-full w-full " id="editor-container">
        <DragDropPaste />
        <ComponentPickerPlugin
          setShowAiMenu={setShowAiWriter}
          showWriteWithAi={true}
          setLastSelection={setLastSelection}
        />
        {/*<AutoLinkPlugin />*/}
        {showLinePlaceholder && (
          <LinePlaceholderPlugin
            paragraphRef={linePlaceholderRef}
            placeholder={`Press 'space' for AI, '/' for commands`}
          />
        )}
        <EditorStorePlugin />
        <OnChangePlugin setShowPlaceholder={setShowPlaceholder} />
        <PrepopulateDocument fraseDocument={documentMetadata} />
        <AutoSaveDocument
          fraseDocument={documentMetadata}
          isLinkEditMode={isLinkEditMode}
        />
        <HistoryPlugin />
        <TopicHighlightPlugin fraseDocument={documentMetadata} />
        <MarkdownTransformerPlugin />
        <RichTextPlugin
          contentEditable={
            <div className="flex flex-row overflow-y-auto overflow-x-hidden h-[calc(100vh-93px)] print:overflow-visible print:h-auto w-full">
              <div
                ref={floatingRef}
                className="relative mx-auto flex w-full max-w-[750px]"
                id="lexical-editor"
              >
                <div
                  ref={blockRef}
                  className="relative w-full"
                  id="lexical-editor-container"
                  style={{ maxWidth: "100%" }}
                >
                  <div className="mt-14"></div>
                  <ImportUrlBadgePlugin
                    fraseDocument={documentMetadata}
                    setShowPlaceholder={setShowPlaceholder}
                  />
                  <TitleInput
                    ref={titleInputRef}
                    content={documentMetadata}
                    setShowPlaceholders={setShowPlaceholder}
                    showPlaceholders={showPlaceholder}
                    setShowLinePlaceholder={setShowLinePlaceholder}
                    readOnly={isReadOnly}
                    type="document"
                  />
                  {!isReadOnly && (
                    <PlaceholderActionsPlugin
                      fraseDocument={documentMetadata}
                      titleInputRef={titleInputRef}
                      showAiMenu={showAiWriter}
                      setShowAiMenu={setShowAiWriter}
                      showPlaceholders={showPlaceholder}
                      setShowPlaceholders={setShowPlaceholder}
                      setShowLinePlaceholder={setShowLinePlaceholder}
                      setLastSelection={setLastSelection}
                    />
                  )}
                  <div hidden={showPlaceholder} className="pb-80">
                    <ContentEditable />
                  </div>
                </div>
              </div>
              {/*process.env.NODE_ENV === "development" && (
                <div className="relative w-[500px] h-full right-0">
                  <TreeViewPlugin lastSelection={lastSelection} />
                </div>
              )*/}
              <div className="sticky top-0 flex flex-col w-[80px]">
                <TopicScorePlugin />
                <WordCountPlugin />
              </div>
            </div>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <MarkdownShortcutPlugin />
        <CodeHighlightPlugin />
        <ListPlugin />
        <CheckListPlugin />
        <ListMaxIndentLevelPlugin maxDepth={7} />
        <TablePlugin
          hasCellMerge={tableCellMerge}
          hasCellBackgroundColor={tableCellBackgroundColor}
        />
        <TableCellResizer />
        <ImagesPlugin />
        <LinkPlugin />
        {!isEditable && <LexicalClickableLinkPlugin />}
        <HorizontalRulePlugin />
        <TabFocusPlugin />
        <TabIndentationPlugin />
        <CollapsiblePlugin />
        {floatingAnchorElem && (
          <>
            <DraggableBlockPlugin anchorElem={blockElem} />
            <AiWriterMenuPlugin
              showMenu={showAiWriter}
              setShowMenu={setShowAiWriter}
              anchorElem={blockElem}
              linePlaceholderRef={linePlaceholderRef}
              setLastSelection={setLastSelection}
              lastSelection={lastSelection}
              isRewriting={isRewriting}
              setIsRewriting={setIsRewriting}
            />
            {/*<AiWriterPlugin
              anchorElement={blockElem}
              setShowAiWriter={setShowAiWriter}
              showAiWriter={showAiWriter}
      />*/}
            <FloatingLinkEditorPlugin
              anchorElem={floatingAnchorElem}
              isLinkEditMode={isLinkEditMode}
              setIsLinkEditMode={setIsLinkEditMode}
            />
            <TableCellActionMenuPlugin
              anchorElem={floatingAnchorElem}
              cellMerge={true}
            />

            <ContinueWritingPlugin
              anchorElem={floatingAnchorElem}
              showAiWriter={showAiWriter}
            />
            <FloatingTextFormatToolbarPlugin
              anchorElem={floatingAnchorElem}
              setIsLinkEditMode={setIsLinkEditMode}
              setShowAiWriter={setShowAiWriter}
              setIsRewriting={setIsRewriting}
              setLastSelection={setLastSelection}
            />
          </>
        )}
      </div>
    </div>
  );
};
